import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useVideo } from '../context/VideoContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiX, FiTag } = FiIcons;

const FilterBar = ({ filters, setFilters }) => {
  const { industries, videoTypes, videos, tags } = useVideo();
  const [inputValue, setInputValue] = useState(filters.search || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    setInputValue(filters.search || '');
  }, [filters.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setFilters({ ...filters, search: value });

    if (value.length >= 2) {
      const lowerValue = value.toLowerCase();
      const filteredTags = tags.filter(t => 
        t.name.toLowerCase().includes(lowerValue)
      ).sort((a, b) => b.count - a.count).slice(0, 5);
      
      setSuggestions(filteredTags);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    setInputValue(suggestion.name);
    setFilters({ ...filters, search: suggestion.name });
    setShowSuggestions(false);
  };

  const clearSearch = () => {
    setInputValue('');
    setFilters({ ...filters, search: '' });
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <motion.div 
      className="glass-card p-6 border-white/5 relative z-40"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Search Input */}
        <div className="relative" ref={searchContainerRef}>
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/30" />
          <input 
            type="text" 
            placeholder="Search keywords..." 
            value={inputValue}
            onChange={handleSearchInput}
            onFocus={() => inputValue.length >= 2 && setShowSuggestions(true)}
            className="w-full pl-12 pr-10 py-3.5 bg-zinc-900/50 border border-white/10 rounded-2xl text-white placeholder-white/20 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none"
          />
          {inputValue && (
            <button 
              onClick={clearSearch}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white transition-colors"
            >
              <SafeIcon icon={FiX} />
            </button>
          )}

          {/* Suggestions Dropdown */}
          <AnimatePresence>
            {showSuggestions && suggestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full left-0 right-0 mt-2 bg-[#18181b] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50 backdrop-blur-xl"
              >
                <div className="py-2">
                  <div className="px-4 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    Top Keywords
                  </div>
                  {suggestions.map((tag) => (
                    <div
                      key={tag.name}
                      onClick={() => selectSuggestion(tag)}
                      className="px-4 py-3 hover:bg-white/5 cursor-pointer flex items-center gap-3 transition-colors border-b border-white/5 last:border-0 group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-slate-500 group-hover:text-orange-500 transition-colors">
                        <SafeIcon icon={FiTag} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-white truncate group-hover:text-orange-400 transition-colors">
                          {tag.name}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {tag.count} video{tag.count !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Industry Filter */}
        <select 
          value={filters.industry} 
          onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
          className="w-full px-4 py-3.5 bg-zinc-900/50 border border-white/10 rounded-2xl text-white focus:border-orange-500 appearance-none custom-select outline-none"
        >
          <option value="">All Industries</option>
          {industries.map((industry) => (
            <option key={industry} value={industry}>{industry}</option>
          ))}
        </select>

        {/* Video Type Filter */}
        <select 
          value={filters.videoType} 
          onChange={(e) => setFilters({ ...filters, videoType: e.target.value })}
          className="w-full px-4 py-3.5 bg-zinc-900/50 border border-white/10 rounded-2xl text-white focus:border-orange-500 appearance-none custom-select outline-none"
        >
          <option value="">All Video Types</option>
          {videoTypes.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>
    </motion.div>
  );
};

export default FilterBar;